import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { findParentNode } from "prosemirror-utils";
import { forwardRef, useCallback, useMemo, useRef } from "react";
import { Box, Flex, Text } from "sprout-ui-primitives";
import { media, themeGet } from "sprout-ui-theme";
import styled, { css } from "styled-components";
import { useSnackbar } from "../../Snackbar";
import { insertImage } from "../extensions/image";
import useSuggestionsMenu from "./useSuggestionsMenu";
const SlashCommandsMenuItem = styled(Flex)`
  cursor: pointer;
  user-select: none;

  ${media.up("medium", "pointer")} {
    :hover {
      background: ${themeGet("colors.black.16")};
    }
  }

  :active {
    background: ${themeGet("colors.black.24")};
  }

  ${({ $selected  })=>$selected && css`
      background: ${themeGet("colors.black.16")};
    `}
`;
const visuallyHiddenStyle = {
    border: "0px",
    clip: "rect(0px, 0px, 0px, 0px)",
    height: "1px",
    width: "1px",
    margin: "-1px",
    padding: "0",
    overflow: "hidden",
    whiteSpace: "nowrap",
    position: "absolute"
};
const SlashCommandsMenu = /*#__PURE__*/ forwardRef((props, ref1)=>{
    const { items , command , editor  } = props;
    const itemsCount = items.length;
    const inputRef = useRef(null);
    const onEnter = useCallback((index)=>{
        var ref;
        const item = items[index];
        if (item.title === "Image") (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.click();
        command(item);
    }, [
        command,
        items
    ]);
    const { selectedIndex  } = useSuggestionsMenu(useMemo(()=>{
        return {
            itemsCount,
            onEnter,
            ref: ref1
        };
    }, [
        itemsCount,
        onEnter,
        ref1
    ]));
    const showSnackbar = useSnackbar();
    const handleImageChange = useCallback((event)=>{
        const files = Array.from(event.target.files || []).filter((file)=>/image/i.test(file.type));
        const { state , view  } = editor;
        const parent = findParentNode((node)=>!!node)(state.selection);
        if (parent) {
            view.dispatch(view.state.tr.insertText("", parent.pos, state.selection.to));
            insertImage({
                showSnackbar,
                view,
                event: event.nativeEvent,
                files,
                pos: parent.pos
            });
        }
        if (inputRef.current) inputRef.current.value = "";
    }, [
        editor,
        showSnackbar
    ]);
    if (items.length === 0) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("input", {
                multiple: true,
                ref: inputRef,
                style: visuallyHiddenStyle,
                type: "file",
                /**
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#attr-accept
         * https://github.com/strapi/strapi/issues/11426#issuecomment-1004890581
         */ accept: "image/png, image/jpg",
                onChange: handleImageChange
            }),
            /*#__PURE__*/ _jsx(Box, {
                bg: "blue.500",
                borderRadius: "12px",
                overflow: "hidden",
                as: "ul",
                p: 1,
                minWidth: "280px",
                children: items.map((item, index)=>{
                    const { title , icon , keybinding  } = item;
                    return /*#__PURE__*/ _jsxs(SlashCommandsMenuItem, {
                        borderRadius: "12px",
                        alignItems: "center",
                        color: "white.100",
                        py: 1,
                        px: 3,
                        $selected: selectedIndex === index,
                        as: "li",
                        onClick: ()=>{
                            onEnter(index);
                        },
                        children: [
                            icon,
                            /*#__PURE__*/ _jsx(Text, {
                                mx: 3,
                                as: "span",
                                fontSize: "15px",
                                fontWeight: "medium",
                                lineHeight: "24px",
                                children: title
                            }),
                            keybinding && /*#__PURE__*/ _jsx(Box, {
                                as: "span",
                                borderRadius: "4px",
                                bg: "white.12",
                                ml: "auto",
                                px: 0,
                                children: /*#__PURE__*/ _jsx(Text, {
                                    fontWeight: "bold",
                                    color: "white.70",
                                    fontSize: "13px",
                                    textAlign: "center",
                                    children: keybinding
                                })
                            })
                        ]
                    }, title);
                })
            })
        ]
    });
});
export default SlashCommandsMenu;
